import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { 
  Search, 
  Filter, 
  CheckCircle, 
  XCircle, 
  Eye,
  Clock,
  MoreVertical,
  Calendar as CalendarIcon,
  AlertCircle,
  Ticket,
  SlidersHorizontal,
  MapPin,
  Users,
  Edit,
  Trash2,
  DollarSign,
  RefreshCw,
  Download,
  QrCode,
  CheckSquare
} from 'lucide-react';
import { eventsService } from '../services/api';
import AdminTicketManager from '../components/AdminTicketManager';

const StatusBadge = ({ status }) => {
  const statusConfig = {
    approved: { color: 'bg-green-100 text-green-800 border border-green-200', icon: CheckCircle },
    pending: { color: 'bg-yellow-100 text-yellow-800 border border-yellow-200', icon: Clock },
    rejected: { color: 'bg-red-100 text-red-800 border border-red-200', icon: XCircle },
    draft: { color: 'bg-gray-100 text-gray-800 border border-gray-200', icon: Clock },
    cancelled: { color: 'bg-red-100 text-red-800 border border-red-200', icon: XCircle },
    completed: { color: 'bg-blue-100 text-blue-800 border border-blue-200', icon: CheckSquare }
  };

  const config = statusConfig[status] || statusConfig.draft;
  const Icon = config.icon;

  return (
    <span className={`inline-flex items-center px-3 py-1.5 rounded-full text-xs font-semibold ${config.color}`}>
      <Icon className="h-3 w-3 mr-1.5" />
      {status.charAt(0).toUpperCase() + status.slice(1)}
    </span>
  );
};

const EventDetailsModal = ({ event, isOpen, onClose, onStatusChange, queryClient }) => {
  const [activeTab, setActiveTab] = useState('details');
  const [loadingStatus, setLoadingStatus] = useState(false);
  const [ticketStats, setTicketStats] = useState(null);
  const [showTicketModal, setShowTicketModal] = useState(false);
  const [ticketData, setTicketData] = useState({
    quantity: 100,
    ticket_type_id: ''
  });

  const { data: eventDetails } = useQuery({
    queryKey: ['event', event?.id],
    queryFn: () => eventsService.getEvent(event.id),
    enabled: !!event && isOpen
  });

  const { data: ticketTypes } = useQuery({
    queryKey: ['ticket-types', event?.id],
    queryFn: () => eventsService.getTickets(event?.id),
    enabled: !!event && isOpen
  });

  const loadTicketStats = async () => {
    if (!event) return;
    try {
      const stats = await eventsService.getTicketStats(event.id);
      setTicketStats(stats);
    } catch (error) {
      console.error('Error loading ticket stats:', error);
    }
  };

  React.useEffect(() => {
    if (event && isOpen && activeTab === 'tickets') {
      loadTicketStats();
    }
  }, [event?.id, isOpen, activeTab]);

  if (!isOpen || !event) return null;

  const handleStatusChange = async (newStatus) => {
    setLoadingStatus(true);
    try {
      await onStatusChange(event.id, newStatus);
      onClose();
    } catch (error) {
      console.error('Error changing status:', error);
      alert(`Failed to update status: ${error.message}`);
    } finally {
      setLoadingStatus(false);
    }
  };

  const handleGenerateTickets = async () => {
    try {
      await eventsService.generateTickets(
        event.id,
        ticketData.quantity,
        ticketData.ticket_type_id || null
      );
      alert(`${ticketData.quantity} tickets generated successfully!`);
      setShowTicketModal(false);
      loadTicketStats();
    } catch (error) {
      alert('Error generating tickets: ' + error.message);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <img
              src={event.cover_image_url || (event.cover_image?.url) || event.cover_image || '/event-placeholder.png'}
              alt={event.title}
              className="h-20 w-20 object-cover rounded-lg shadow-xs border border-gray-200"
            />
            <div>
              <h2 className="text-xl font-semibold text-gray-900">{event.title}</h2>
              <p className="text-gray-600 text-sm">{formatDate(event.start_date)} • {event.city}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <XCircle className="h-6 w-6" />
          </button>
        </div>

        <div className="border-b border-gray-200">
          <div className="px-6 flex space-x-8">
            {['details', 'tickets', 'actions'].map(tab => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`py-4 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === tab
                    ? 'border-[#1d1d1d] text-[#1d1d1d]'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                {tab === 'details' ? 'Event Details' : 
                 tab === 'tickets' ? 'Ticket Management' : 'Actions'}
              </button>
            ))}
          </div>
        </div>

        <div className="p-6 max-h-[60vh] overflow-y-auto">
          {activeTab === 'details' && (
            <div className="space-y-6">
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <h3 className="text-sm font-medium text-gray-500 mb-2">Basic Information</h3>
                  <dl className="space-y-2">
                    <div><dt className="text-xs text-gray-500">Title</dt><dd className="text-sm font-medium">{event.title}</dd></div>
                    <div><dt className="text-xs text-gray-500">Event ID</dt><dd className="text-sm font-medium font-mono">{event.event_id}</dd></div>
                    <div><dt className="text-xs text-gray-500">Type</dt><dd className="text-sm font-medium">{event.event_type_name || 'N/A'}</dd></div>
                    <div><dt className="text-xs text-gray-500">Status</dt><dd className="text-sm font-medium"><StatusBadge status={event.status} /></dd></div>
                  </dl>
                </div>
                <div>
                  <h3 className="text-sm font-medium text-gray-500 mb-2">Date & Location</h3>
                  <dl className="space-y-2">
                    <div className="flex items-start">
                      <CalendarIcon className="h-3 w-3 mr-2 mt-1 text-gray-400" />
                      <div>
                        <dt className="text-xs text-gray-500">Date</dt>
                        <dd className="text-sm font-medium">{formatDate(event.start_date)}</dd>
                      </div>
                    </div>
                    <div className="flex items-start">
                      <MapPin className="h-3 w-3 mr-2 mt-1 text-gray-400" />
                      <div>
                        <dt className="text-xs text-gray-500">Venue</dt>
                        <dd className="text-sm font-medium">{event.venue_name}, {event.city}</dd>
                      </div>
                    </div>
                    <div>
                      <dt className="text-xs text-gray-500">Ticket Type</dt>
                      <dd className="text-sm font-medium capitalize">{event.ticket_type}</dd>
                    </div>
                    <div>
                      <dt className="text-xs text-gray-500">Price</dt>
                      <dd className="text-sm font-medium">
                        {event.ticket_type === 'free' ? 'FREE' : `GHS ${event.current_price || event.price}`}
                      </dd>
                    </div>
                  </dl>
                </div>
              </div>

              {event.description && (
                <div>
                  <h3 className="text-sm font-medium text-gray-500 mb-2">Description</h3>
                  <p className="text-sm text-gray-700 leading-relaxed">{event.description}</p>
                </div>
              )}

              {/* Hosts & Featured Artists */}
              {(event.hosts_count > 0 || event.featured_artists_count > 0) && (
                <div className="grid grid-cols-2 gap-6">
                  {event.hosts_count > 0 && (
                    <div>
                      <h3 className="text-sm font-medium text-gray-500 mb-2">Hosts ({event.hosts_count})</h3>
                      <div className="space-y-2">
                        {eventDetails?.hosts_details?.map(host => (
                          <div key={host.id} className="flex items-center space-x-2">
                            <Users className="h-4 w-4 text-gray-400" />
                            <span className="text-sm text-gray-700">{host.full_name || host.username}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {event.featured_artists_count > 0 && (
                    <div>
                      <h3 className="text-sm font-medium text-gray-500 mb-2">Featured Artists ({event.featured_artists_count})</h3>
                      <div className="space-y-2">
                        {eventDetails?.featured_artists_details?.map(artist => (
                          <div key={artist.id} className="flex items-center space-x-2">
                            <Users className="h-4 w-4 text-gray-400" />
                            <span className="text-sm text-gray-700">{artist.stage_name}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Ticket Information */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="text-sm font-medium text-gray-500 mb-3">Ticket Information</h3>
                <div className="grid grid-cols-3 gap-4">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-gray-900">{event.capacity || '∞'}</div>
                    <div className="text-xs text-gray-500">Capacity</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-green-600">{event.tickets_sold || 0}</div>
                    <div className="text-xs text-gray-500">Tickets Sold</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-gray-900">
                      {event.is_sold_out ? 'SOLD OUT' : event.capacity ? event.capacity - (event.tickets_sold || 0) : '∞'}
                    </div>
                    <div className="text-xs text-gray-500">Available</div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'tickets' && (
  <AdminTicketManager 
    eventId={event.id} 
    eventDetails={eventDetails} 
  />
)}

          {activeTab === 'tickets' && (
            <div className="space-y-6">
              <div className="flex justify-between items-center">
                <h3 className="text-sm font-medium text-gray-500">Ticket Management</h3>
                <button
                  onClick={() => setShowTicketModal(true)}
                  className="bg-[#1d1d1d] text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-800 transition-colors"
                >
                  Generate Tickets
                </button>
              </div>

              {ticketStats && (
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="text-sm font-medium text-gray-500 mb-3">Ticket Statistics</h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="text-center">
                      <div className="text-xl font-bold text-gray-900">{ticketStats.total_tickets || 0}</div>
                      <div className="text-xs text-gray-500">Total Tickets</div>
                    </div>
                    <div className="text-center">
                      <div className="text-xl font-bold text-green-600">{ticketStats.purchased_tickets || 0}</div>
                      <div className="text-xs text-gray-500">Purchased</div>
                    </div>
                    <div className="text-center">
                      <div className="text-xl font-bold text-blue-600">{ticketStats.redeemed_tickets || 0}</div>
                      <div className="text-xs text-gray-500">Redeemed</div>
                    </div>
                    <div className="text-center">
                      <div className="text-xl font-bold text-[#ff9702]">GHS {ticketStats.revenue?.toFixed(2) || '0.00'}</div>
                      <div className="text-xs text-gray-500">Revenue</div>
                    </div>
                  </div>
                </div>
              )}

              {ticketTypes && ticketTypes.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-gray-500 mb-3">Ticket Types</h4>
                  <div className="space-y-3">
                    {ticketTypes.map(ticketType => (
                      <div key={ticketType.id} className="flex items-center justify-between p-3 bg-white border border-gray-200 rounded-lg">
                        <div>
                          <h5 className="font-medium text-gray-900">{ticketType.name}</h5>
                          <p className="text-sm text-gray-500">GHS {ticketType.price}</p>
                        </div>
                        <div className="text-right">
                          <div className="text-sm font-medium text-gray-900">
                            {ticketType.remaining === "Unlimited" ? "Unlimited" : `${ticketType.remaining} available`}
                          </div>
                          <div className="text-xs text-gray-500">{ticketType.sold_count} sold</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Ticket Redemption */}
              <div className="border-t border-gray-200 pt-6">
                <h4 className="text-sm font-medium text-gray-500 mb-3">Ticket Redemption</h4>
                <div className="flex space-x-3">
                  <input
                    type="text"
                    placeholder="Enter ticket code..."
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1d1d1d]"
                  />
                  <button className="bg-green-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-green-700 transition-colors">
                    Redeem
                  </button>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'actions' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-sm font-medium text-gray-500 mb-4">Change Event Status</h3>
                <div className="grid grid-cols-3 gap-4">
                  <button
                    onClick={() => handleStatusChange('approved')}
                    disabled={event.status === 'approved' || loadingStatus}
                    className="p-4 bg-green-50 border border-green-200 rounded-lg text-center disabled:opacity-50 hover:bg-green-100 transition-colors"
                  >
                    {loadingStatus ? (
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-green-600 mx-auto mb-2"></div>
                    ) : (
                      <>
                        <CheckCircle className="h-6 w-6 text-green-600 mx-auto mb-2" />
                        <div className="text-sm font-medium text-green-800">Approve</div>
                        <div className="text-xs text-green-600 mt-1">Make event public</div>
                      </>
                    )}
                  </button>
                  
                  <button
                    onClick={() => handleStatusChange('draft')}
                    disabled={event.status === 'draft' || loadingStatus}
                    className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg text-center disabled:opacity-50 hover:bg-yellow-100 transition-colors"
                  >
                    {loadingStatus ? (
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-yellow-600 mx-auto mb-2"></div>
                    ) : (
                      <>
                        <Clock className="h-6 w-6 text-yellow-600 mx-auto mb-2" />
                        <div className="text-sm font-medium text-yellow-800">Put on Hold</div>
                        <div className="text-xs text-yellow-600 mt-1">Change to draft status</div>
                      </>
                    )}
                  </button>
                  
                  <button
                    onClick={() => handleStatusChange('cancelled')}
                    disabled={event.status === 'cancelled' || loadingStatus}
                    className="p-4 bg-red-50 border border-red-200 rounded-lg text-center disabled:opacity-50 hover:bg-red-100 transition-colors"
                  >
                    {loadingStatus ? (
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-red-600 mx-auto mb-2"></div>
                    ) : (
                      <>
                        <XCircle className="h-6 w-6 text-red-600 mx-auto mb-2" />
                        <div className="text-sm font-medium text-red-800">Cancel</div>
                        <div className="text-xs text-red-600 mt-1">Cancel this event</div>
                      </>
                    )}
                  </button>
                </div>
              </div>

              <div className="border-t border-gray-200 pt-6">
                <h3 className="text-sm font-medium text-gray-500 mb-4">Other Actions</h3>
                <div className="grid grid-cols-2 gap-4">
                  <Link
                    to={`/admin/events/${event.id}/edit`}
                    className="inline-flex items-center px-4 py-2.5 border border-gray-300 text-sm font-semibold rounded-xl text-gray-700 bg-white hover:bg-gray-50 transition-all duration-200 shadow-sm hover:shadow-md no-underline"
                  >
                    <Edit className="h-4 w-4 mr-2" />
                    Edit Event
                  </Link>
                  <button 
                    onClick={() => {
                      if (window.confirm('Are you sure you want to delete this event?')) {
                        eventsService.deleteEvent(event.id)
                          .then(() => {
                            alert('Event deleted successfully');
                            onClose();
                            queryClient.invalidateQueries({ queryKey: ['events'] });
                          })
                          .catch(() => alert('Error deleting event'));
                      }
                    }}
                    className="p-3 bg-red-50 border border-red-200 rounded-lg text-center hover:bg-red-100 transition-colors"
                  >
                    <Trash2 className="h-5 w-5 text-red-600 mx-auto mb-1" />
                    <div className="text-sm font-medium text-red-800">Delete Event</div>
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Generate Tickets Modal */}
      {showTicketModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[60]">
          <div className="bg-white rounded-2xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Generate Tickets</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Quantity</label>
                <input
                  type="number"
                  min="1"
                  value={ticketData.quantity}
                  onChange={(e) => setTicketData({...ticketData, quantity: parseInt(e.target.value)})}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1d1d1d]"
                />
              </div>
              {ticketTypes && ticketTypes.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Ticket Type (Optional)</label>
                  <select
                    value={ticketData.ticket_type_id}
                    onChange={(e) => setTicketData({...ticketData, ticket_type_id: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1d1d1d]"
                  >
                    <option value="">General Admission</option>
                    {ticketTypes.map(type => (
                      <option key={type.id} value={type.id}>{type.name} (GHS {type.price})</option>
                    ))}
                  </select>
                </div>
              )}
              <div className="flex space-x-3 pt-4">
                <button
                  onClick={() => setShowTicketModal(false)}
                  className="flex-1 bg-gray-100 text-gray-700 px-4 py-2 rounded-lg font-medium hover:bg-gray-200 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleGenerateTickets}
                  className="flex-1 bg-[#1d1d1d] text-white px-4 py-2 rounded-lg font-medium hover:bg-gray-800 transition-colors"
                >
                  Generate
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default function EventsManagement() {  
  const [filters, setFilters] = useState({
    search: '',
    status: '',
    event_type: '',
    upcoming_only: 'true'
  });
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const queryClient = useQueryClient();

  const { data: eventsData, isLoading, error } = useQuery({
    queryKey: ['events', filters],
    queryFn: () => eventsService.getAll(filters)
  });

  const { data: pendingEvents } = useQuery({
    queryKey: ['pending-events'],
    queryFn: () => eventsService.getPending()
  });

  const updateStatusMutation = useMutation({
  mutationFn: ({ eventId, status, reason = '' }) => 
    eventsService.adminUpdateStatus(eventId, status, reason),
  onSuccess: () => {
    queryClient.invalidateQueries({ queryKey: ['events'] });
    queryClient.invalidateQueries({ queryKey: ['pending-events'] });
    alert('Event status updated successfully!');
  },
  onError: (error) => {
    console.error('Status update failed:', error);
    alert(`Failed to update status: ${error.response?.data?.error || error.message || 'Unknown error'}`);
  }
});

const handleStatusChange = async (eventId, newStatus) => {
  let reason = '';
  if (newStatus === 'rejected' || newStatus === 'cancelled') {
    reason = window.prompt('Please provide a reason:');
    if (reason === null) return;
  }
  
  updateStatusMutation.mutate({ eventId, status: newStatus, reason });
};

  const handleViewDetails = (event) => {
    setSelectedEvent(event);
    setShowDetailsModal(true);
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric'
    });
  };

  const events = eventsData?.results || eventsData || [];

  if (error) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <AlertCircle className="mx-auto h-16 w-16 text-red-400 mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Error loading events</h3>
          <p className="text-gray-600 font-medium">Please try again later.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Events Management</h1>
          <p className="text-gray-600 font-medium">Manage and review all events on the platform</p>
        </div>
        <div className="flex items-center space-x-4">
          <div className="text-sm text-gray-500 bg-gray-100 px-4 py-2 rounded-xl font-semibold">
            {pendingEvents?.length || 0} events pending review
          </div>
          <Link
            to="/admin/create-event"
            className="bg-gradient-to-r from-[#ff9702] to-[#ffaa33] text-white px-5 py-2.5 rounded-xl text-sm font-semibold hover:shadow-lg transition-all duration-200 shadow-md hover:shadow-lg flex items-center no-underline"
          >
            <CalendarIcon className="h-4 w-4 mr-2" />
            Create Event
          </Link>
        </div>
      </div>

      <div className="bg-white rounded-2xl shadow-xs border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center">
            <SlidersHorizontal className="h-5 w-5 mr-2 text-[#1d1d1d]" />
            Filters
          </h3>
          <button
            onClick={() => setFilters({ search: '', status: '', event_type: '', upcoming_only: 'true' })}
            className="text-sm text-gray-500 hover:text-gray-700 font-medium"
          >
            Clear all
          </button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Search Events</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search by title or venue..."
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                className="pl-10 w-full rounded-xl border border-gray-300 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#1d1d1d] focus:border-[#1d1d1d] transition-all duration-200"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Status</label>
            <select
              value={filters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
              className="w-full rounded-xl border border-gray-300 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#1d1d1d] focus:border-[#1d1d1d] transition-all duration-200"
            >
              <option value="">All Status</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="draft">Draft</option>
              <option value="cancelled">Cancelled</option>
              <option value="completed">Completed</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Event Type</label>
            <select
              value={filters.event_type}
              onChange={(e) => handleFilterChange('event_type', e.target.value)}
              className="w-full rounded-xl border border-gray-300 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#1d1d1d] focus:border-[#1d1d1d] transition-all duration-200"
            >
              <option value="">All Types</option>
              <option value="concert">Concert</option>
              <option value="festival">Festival</option>
              <option value="workshop">Workshop</option>
              <option value="meetup">Meetup</option>
              <option value="virtual">Virtual</option>
            </select>
          </div>

          <div className="flex items-end">
            <button
              onClick={() => setFilters({ search: '', status: '', event_type: '', upcoming_only: 'true' })}
              className="w-full bg-gray-100 text-gray-700 px-4 py-3 rounded-xl hover:bg-gray-200 transition-all duration-200 font-semibold"
            >
              Reset Filters
            </button>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-2xl shadow-xs border border-gray-200 overflow-hidden">
        <div className="px-6 py-5 border-b border-gray-100">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900">
              All Events ({events.length})
            </h2>
            <div className="text-sm text-gray-500 font-medium">Sorted by: Date</div>
          </div>
        </div>

        {isLoading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1d1d1d]"></div>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {events.map((event) => (
              <div key={event.id} className="px-6 py-5 hover:bg-gray-50 transition-colors duration-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className="relative">
                      <img
                        src={event.cover_image_url || (event.cover_image?.url) || event.cover_image || '/event-placeholder.png'}
                        alt={event.title}
                        className="h-20 w-20 object-cover rounded-lg shadow-xs border border-gray-200"
                      />
                      {event.status === 'pending' && (
                        <div className="absolute -top-1 -right-1">
                          <div className="bg-yellow-500 rounded-full p-1 shadow-xs border border-white">
                            <Clock className="h-2 w-2 text-white" />
                          </div>
                        </div>
                      )}
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-1">{event.title}</h3>
                      <p className="text-sm text-gray-600 font-medium mb-2">
                        <span className="flex items-center">
                          <CalendarIcon className="h-3 w-3 mr-1" />
                          {formatDate(event.start_date)} • {event.venue_name}, {event.city}
                        </span>
                      </p>
                      <div className="flex items-center space-x-4">
                        <StatusBadge status={event.status} />
                        <span className="text-sm text-gray-500 bg-gray-100 px-3 py-1 rounded-full font-medium">
                          {event.event_type_name || 'No Type'}
                        </span>
                        <span className="text-sm font-semibold text-[#ff9702] flex items-center">
                          <DollarSign className="h-3 w-3 mr-1" />
                          {event.ticket_type === 'free' ? 'FREE' : `GHS ${event.current_price || event.price}`}
                        </span>
                        <span className="text-sm text-gray-500 font-medium flex items-center">
                          <Ticket className="h-3 w-3 mr-1" />
                          {event.tickets_sold || 0} sold
                        </span>
                        <span className="text-sm text-gray-500 font-medium">ID: {event.event_id || 'N/A'}</span>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center space-x-3">
                    {event.status === 'pending' && (
                      <>
                        <button
                          onClick={() => handleStatusChange(event.id, 'approved')}
                          disabled={updateStatusMutation.isLoading}
                          className="inline-flex items-center px-4 py-2.5 border border-transparent text-sm font-semibold rounded-xl text-white bg-green-600 hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-md hover:shadow-lg"
                        >
                          {updateStatusMutation.isLoading ? (
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                          ) : (
                            <CheckCircle className="h-4 w-4 mr-2" />
                          )}
                          Approve
                        </button>
                        <button
                          onClick={() => handleStatusChange(event.id, 'draft')}
                          disabled={updateStatusMutation.isLoading}
                          className="inline-flex items-center px-4 py-2.5 border border-gray-300 text-sm font-semibold rounded-xl text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-sm hover:shadow-md"
                        >
                          {updateStatusMutation.isLoading ? (
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-400 mr-2"></div>
                          ) : (
                            <Clock className="h-4 w-4 mr-2" />
                          )}
                          Put on Hold
                        </button>
                      </>
                    )}
                    
                    <button 
                      onClick={() => handleViewDetails(event)}
                      className="inline-flex items-center px-4 py-2.5 border border-gray-300 text-sm font-semibold rounded-xl text-gray-700 bg-white hover:bg-gray-50 transition-all duration-200 shadow-sm hover:shadow-md"
                    >
                      <Eye className="h-4 w-4 mr-2" />
                      View Details
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {events.length === 0 && !isLoading && (
          <div className="text-center py-16">
            <CalendarIcon className="mx-auto h-16 w-16 text-gray-300 mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No events found</h3>
            <p className="text-gray-600 font-medium mb-4">Try adjusting your search or filter criteria.</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={() => setFilters({ search: '', status: '', event_type: '', upcoming_only: 'true' })}
                className="bg-[#1d1d1d] text-white px-6 py-3 rounded-xl text-sm font-semibold hover:bg-gray-800 transition-all duration-200"
              >
                Clear all filters
              </button>
              <Link
                to="/admin/create-event"
                className="bg-gradient-to-r from-[#ff9702] to-[#ffaa33] text-white px-6 py-3 rounded-xl text-sm font-semibold hover:shadow-lg transition-all duration-200 no-underline text-center"
              >
                Create First Event
              </Link>
            </div>
          </div>
        )}
      </div>

      <EventDetailsModal
        event={selectedEvent}
        isOpen={showDetailsModal}
        onClose={() => {
          setShowDetailsModal(false);
          setSelectedEvent(null);
        }}
        onStatusChange={handleStatusChange}
        queryClient={queryClient}
      />
    </div>
  );
}